import { apiFetch } from "@lattice-php/core/api";
import type { Option } from "@lattice-php/core";
import { translate } from "@lattice-php/ui/i18n";
import { DEFAULT_COLUMN_WIDTH } from "@lattice-php/ui/lib/column-sizing";
import type { ColumnWidth } from "@lattice-php/ui/types";
import { isEmptyMember } from "./filter-values";
import type { TableColumn, TableSort, TableQuery } from "@lattice-php/table/types";
import type { Op, Side } from "@lattice-php/ui";

export function getColumnSort(query: TableQuery, column: TableColumn): TableSort | undefined {
  return query.sorts.find((currentSort) => currentSort.key === column.key);
}

export function buildEndpoint(endpoint: string, query: TableQuery): string {
  const url = new URL(endpoint, window.location.origin);

  if (query.filters.length > 0) {
    url.searchParams.set("filter", serializeFilters(query));
  }

  if (query.sorts.length > 0) {
    url.searchParams.set("sort", serializeSorts(query));
  }

  if (query.search !== "") {
    url.searchParams.set("q", query.search);
  }

  appendTableFilters(url, query.tableFilters);

  url.searchParams.set("page", String(query.page));
  url.searchParams.set("per_page", String(query.perPage));

  if (query.mode != null) {
    url.searchParams.set("mode", query.mode);
  }

  return `${url.pathname}${url.search}`;
}

export function appendTableFilters(url: URL, tableFilters: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(getTableFilterParams(tableFilters))) {
    appendTableFilterParam(url, `tf[${key}]`, value);
  }
}

function getTableFilterParams(tableFilters: Record<string, unknown>): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(tableFilters)) {
    const normalized = normalizeTableFilterValue(value);

    if (normalized !== undefined) {
      params[key] = normalized;
    }
  }

  return params;
}

function normalizeTableFilterValue(value: unknown): unknown | undefined {
  if (isEmptyMember(value) || Array.isArray(value) || typeof value !== "object") {
    return undefined;
  }

  const entries = Object.entries(value)
    .map(([subKey, subValue]) => [subKey, normalizeTableFilterMember(subValue)] as const)
    .filter((entry): entry is readonly [string, unknown] => entry[1] !== undefined);

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizeTableFilterMember(value: unknown): unknown | undefined {
  if (isEmptyMember(value)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    const values = value.map(normalizeTableFilterMember).filter((item) => item !== undefined);

    return values.length > 0 ? values : undefined;
  }

  if (typeof value === "object") {
    if (value === null) {
      return undefined;
    }

    const entries = Object.entries(value)
      .map(([key, item]) => [key, normalizeTableFilterMember(item)] as const)
      .filter((entry): entry is readonly [string, unknown] => entry[1] !== undefined);

    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
  }

  return String(value);
}

export function appendTableFilterParam(url: URL, key: string, value: unknown): void {
  if (Array.isArray(value)) {
    for (const item of value) {
      appendTableFilterParam(url, `${key}[]`, item);
    }

    return;
  }

  if (typeof value === "object" && value !== null) {
    for (const [subKey, subValue] of Object.entries(value)) {
      appendTableFilterParam(url, `${key}[${subKey}]`, subValue);
    }

    return;
  }

  url.searchParams.append(key, String(value));
}

/**
 * The `_sub=search` sub-request every searchable select/filter/board field
 * uses to look up options against a component's own endpoint.
 */
export async function fetchFilterOptions(
  endpoint: string | null,
  componentRef: string,
  searchKey: string,
  query: string,
  signal?: AbortSignal,
): Promise<Option[]> {
  if (!endpoint) {
    return [];
  }

  const url = new URL(endpoint, window.location.origin);
  url.searchParams.set("_sub", "search");
  url.searchParams.set("_target", searchKey);
  url.searchParams.set("_q", query);

  const response = await apiFetch(`${url.pathname}${url.search}`, {
    ref: componentRef,
    signal,
    throwOnError: false,
  });

  if (!response.ok) {
    return [];
  }

  const body = (await response.json()) as { options?: Option[] };

  return body.options ?? [];
}

export function getQueryParams(query: TableQuery): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  if (query.filters.length > 0) {
    params.filter = serializeFilters(query);
  }

  if (query.sorts.length > 0) {
    params.sort = serializeSorts(query);
  }

  const tableFilters = getTableFilterParams(query.tableFilters);

  if (Object.keys(tableFilters).length > 0) {
    params.tf = tableFilters;
  }

  if (query.search !== "") {
    params.q = query.search;
  }

  return params;
}

/**
 * `getQueryParams()` plus `page`/`per_page`, the two params only a URL-synced
 * table restores: `page` when it isn't the implicit first page, `per_page`
 * when it differs from the definition's default (so a clean URL round-trips
 * to a no-op write).
 */
export function getUrlQueryParams(
  query: TableQuery,
  defaultPerPage: number,
): Record<string, unknown> {
  const params = getQueryParams(query);

  if (query.page > 1) {
    params.page = query.page;
  }

  if (query.perPage !== defaultPerPage) {
    params.per_page = query.perPage;
  }

  return params;
}

function serializeFilters(query: TableQuery): string {
  return query.filters
    .map((clause) => `${clause.field}:${clause.operator}:${encodeURIComponent(clause.value)}`)
    .join(",");
}

function serializeSorts(query: TableQuery): string {
  return query.sorts
    .map((sort) => (sort.direction === "desc" ? `-${sort.key}` : sort.key))
    .join(",");
}

const operatorLabels: Record<Op, string> = {
  contains: "contains",
  starts_with: "starts with",
  ends_with: "ends with",
  eq: "equals",
  neq: "not equals",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  in: "in",
  not_in: "not in",
  before: "before",
  after: "after",
  empty: "is empty",
  filled: "is not empty",
};

export const VALUELESS_FILTER_OPERATORS = new Set<string>(["empty", "filled"] satisfies Op[]);

export function operatorLabel(operator: string): string {
  return translate(
    "lattice",
    `table.operators.${operator}`,
    operatorLabels[operator as Op] ?? operator,
  );
}

export function getSortDirectionLabel(direction: string): string {
  return direction === "desc" ? "descending" : "ascending";
}

export function nextSort(sorts: TableSort[], column: TableColumn): TableSort[] {
  if (!column.props.sortable) {
    return sorts;
  }

  const currentSort = sorts.find((sort) => sort.key === column.key);
  const remainingSorts = sorts.filter((sort) => sort.key !== column.key);

  if (!currentSort) {
    return [...sorts, { key: column.key, direction: "asc" }];
  }

  if (currentSort.direction === "asc") {
    return [...remainingSorts, { key: column.key, direction: "desc" }];
  }

  return remainingSorts;
}

export function getVisiblePages(currentPage: number, lastPage: number): number[] {
  if (lastPage <= 5) {
    return Array.from({ length: lastPage }, (_, index) => index + 1);
  }

  const start = Math.max(1, Math.min(currentPage - 2, lastPage - 4));

  return Array.from({ length: 5 }, (_, index) => start + index);
}

export function orderPinnedColumns<T>(columns: T[], pinOf: (column: T) => Side | null): T[] {
  const start: T[] = [];
  const middle: T[] = [];
  const end: T[] = [];

  for (const column of columns) {
    const pin = pinOf(column);

    if (pin === "start") {
      start.push(column);
    } else if (pin === "end") {
      end.push(column);
    } else {
      middle.push(column);
    }
  }

  return [...start, ...middle, ...end];
}

export function getTableSizingColumns(
  columns: TableColumn[],
  pinOf?: (column: TableColumn) => Side | null,
) {
  return columns.map((column) => ({
    key: column.key,
    label: column.props.label,
    pin: pinOf?.(column) ?? undefined,
    width: columnWidthOrDefault(column),
  }));
}

function columnWidthOrDefault(column: TableColumn): ColumnWidth {
  return (column.props.width ?? DEFAULT_COLUMN_WIDTH) as ColumnWidth;
}
